import { Controller } from "@hotwired/stimulus"

// First-run hints on a shared family tree (/family/:token).
//
// This replaced a welcome modal. The modal had to be read and dismissed before
// you could see anything, described controls in words while hiding them behind
// itself, and asked a reader who did not come looking for a product to sit
// through an introduction to one. These point AT the three controls instead,
// from beside them, over a page you can already see.
//
// They are not a tour: no steps, no Next, nothing to complete. All three show
// at once and the whole set goes as soon as you touch the tree - the first pan,
// zoom, search or click is proof the hints have done their job, and someone who
// starts using it immediately should not have to tidy up after an explanation
// they did not need.
//
// Shown once, remembered against the share token: per-link (a different
// relative's link is a different first visit) and per-browser, which is all a
// visitor with no account has. localStorage THROWS rather than returning null
// in a private window, so every access is wrapped and a failure degrades to
// showing the hints again - never to a broken page.
export default class extends Controller {
    static targets = ["hint"]
    static values = { key: String }

    connect() {
        // The controller also removes a missing sender photo so the initial
        // underneath remains the whole fallback. A focused/path link has no
        // first-run hints or storage key, but it still needs that image error
        // handler, so the controller is mounted on every public share.
        if (!this.hasKeyValue || !this.keyValue) return

        if (this._seen()) {
            this.hintTargets.forEach((hint) => hint.remove())
            return
        }

        this._remember()
        // Marked so the CSS can animate them in only on the run that shows
        // them, and so a test has a signal that is not "the element exists".
        this.element.dataset.hintsShown = "true"

        this._dismiss = () => this.dismissAll()
        // Anything that counts as using the tree. Capture, so a click on a card
        // that stops propagating still clears them; passive, so listening never
        // delays a scroll.
        const opts = { capture: true, passive: true, once: true }
        this._events = ["pointerdown", "wheel", "keydown", "touchstart"]
        this._events.forEach((name) => this.element.addEventListener(name, this._dismiss, opts))
    }

    disconnect() {
        this._unbind()
    }

    dismissAll(event) {
        event?.preventDefault()
        this._unbind()
        delete this.element.dataset.hintsShown
        this.hintTargets.forEach((hint) => hint.remove())
    }

    avatarError(event) {
        event.currentTarget.remove()
    }

    _unbind() {
        if (!this._dismiss) return
        this._events.forEach((name) => this.element.removeEventListener(name, this._dismiss, { capture: true }))
        this._dismiss = null
    }

    _storageKey() {
        return `jane-family-hints:${this.keyValue}`
    }

    _seen() {
        try {
            return window.localStorage.getItem(this._storageKey()) === "1"
        } catch {
            return false
        }
    }

    _remember() {
        try {
            window.localStorage.setItem(this._storageKey(), "1")
        } catch {
            // A visitor who cannot store the flag sees the hints again. That is
            // the acceptable failure.
        }
    }
}
